import React, { useState } from "react";
import {
  Typography,
  Box,
  Container,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  useMediaQuery,
  useTheme,
  Modal, // Add Modal component
  IconButton, // Add IconButton for the eye button
  List, // For listing job responsibilities
  ListItem, // For list items
  ListItemText, // For list item text
  Fade, // For transition effects
  Snackbar, // Add this import for the popup notification
  Alert, // Add this for styling the notification
} from "@mui/material";
import GitHubIcon from "@mui/icons-material/GitHub";
import LaunchIcon from "@mui/icons-material/Launch";
import VisibilityIcon from "@mui/icons-material/Visibility"; // Add eye icon
import CloseIcon from "@mui/icons-material/Close"; // For closing the modal
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
// import ScrollRevealSection from "../common/ScrollRevealSection";
import AnimatedSection from "../common/AnimatedSection";
import LockedIn from "../../assets/lockedin.png";
import Allocator from "../../assets/image.png";
import MMwave from "../../assets/mmWave.jpg";
import CNN from "../../assets/logos/image.png";
import CyberSec from "../../assets/Final.jpg";
import NeuralNet from "../../assets/An-artificial-neural-network.png";
import SharePlay from "../../assets/SharePlay.webp";
import ResQme from "../../assets/resqme.jpg";
import CloudRL from "../../assets/CloudRL.png";
// import CloudRLReport from "../../assets/aramtel_rajsekar_joseph55_final_project_report.pdf";
import Watcher from "../../assets/1762914954997.jpeg";
import Tiresias from "../../assets/tiresias.png";
import Symbiote from "../../assets/symbiote_logo.png";
import CarProx from "../../assets/carbon_proxy_logo.jpg";
import Dots from "../../assets/gallery.jpg";
import LearnEasy from "../../assets/galleryLH.jpg";

const projects = [
  {
    id: 17,
    title: "Payments Platform Prototype",
    placeholder: "PAYMENTS",
    description:
      "Technical payments prototype spanning identity, secure sessions, verification, and a three-tier web architecture; a possible future startup concept.",
    image: null,
    tags: ["React", "TypeScript", "Node.js", "Go", "PostgreSQL", "Redis"],
    github: "",
    demo: "https://www.routmoney.com/",
    details: {
      company: "Independent Project",
      period: "Apr 2026 - Present",
      responsibilities: [
        "Developed a React/TypeScript SPA, Node/Express backend-for-frontend, and Go verification service with PostgreSQL and Redis.",
        "Reworked browser authentication around HttpOnly, SameSite cookies, CSRF protection, and opaque rotating session references backed by an encrypted Redis-scalable store.",
        "Built password, Google OAuth, magic link, email OTP, TOTP, SMS, and WebAuthn passkey paths with per-device sessions, global revocation, and an audit trail.",
        "Implemented IP-and-account rate limiting with progressive lockouts and modeled PostgreSQL data with row-level security and forward-only migrations.",
        "Designed schema-driven KYC onboarding for four regions and a provider-agnostic Go verification layer with Mock, Persona, and Stripe Identity adapters.",
        "Integrated document and selfie capture, liveness checks, signed webhooks, and CI security checks.",
        "Exploring the possibility of turning this prototype into a startup in the future.",
      ],
    },
  },
  {
    id: 1,
    title: "Resume Tailoring MCP Server",
    description:
      "An MCP server that tailors resumes, CVs, and cover letters to pasted job descriptions and compiles ATS-optimized PDFs.",
    image: null,
    tags: ["TypeScript", "Node.js", "MCP", "Zod", "LaTeX"],
    github: "https://github.com/abhi-ramtel/mcp-overleaf-server",
    demo: "",
    details: {
      company: "Independent Project",
      period: "Jul 2026 - Present",
      responsibilities: [
        "Built a Model Context Protocol server exposing 11 tools that tailor a resume, CV, and cover letter to any pasted job description and compile them into ATS-optimized PDFs from MCP clients including Claude Desktop, Cursor, and VS Code.",
        "Engineered an anti-fabrication provenance layer: every generated bullet cites a stable ID from the master CV, while the renderer rejects unknown IDs and flags unsupported quantitative claims or skills.",
        "Implemented a LaTeX injection engine that rewrites section bodies while preserving existing preambles, macros, fonts, and spacing, with fallback placeholder templates.",
        "Designed a batch pipeline for up to 10 postings per run, with overlap-coefficient role clustering, cached tailored content, deterministic ATS keyword extraction, coverage scoring, and gap analysis.",
        "Added automatic application logging and Overleaf Git synchronization with cached failure handling, shipped with 43 automated tests covering parsing, provenance rules, and real PDF compilation.",
      ],
    },
  },
  {
    id: 2,
    title: "Dots",
    description:
      "Award-winning AR accessibility assistant for indoor wayfinding with spatial computing, voice interaction, and real-time navigation.",
    image: Dots,
    tags: ["Swift", "SwiftUI", "ARKit", "RoomPlan", "RealityKit", "Gemini API"],
    github: "",
    demo: "https://devpost.com/software/dots-y5r21j",
    details: {
      company: "UCLA Hacks 2026",
      period: "Apr 2026",
      responsibilities: [
        "Placed 1st in Catalyst for Care and 3rd in UCLA Hacks Agentverse for an AR accessibility assistant that improves indoor wayfinding.",
        "Built compass-consistent AR guidance that snaps a live pose to a planned path, uses 0.5m breadcrumb checkpoints, and generates cues from camera orientation rather than raw drift.",
        "Added real-time safety and usability features: YOLO-based obstacle warnings, enlarged interactive minimaps, live route visualization, and persistent room-model relabeling.",
      ],
    },
  },
  {
    id: 3,
    title: "CarbonProxy",
    description:
      "HackDuke-winning carbon-aware middleware that reduces LLM inference cost through semantic prompt compression and routing.",
    image: CarProx,
    tags: ["Python", "React", "FastAPI", "SQLite", "NumPy", "Gemini API"],
    github: "https://github.com/octomaanav/CarbonProxy.git",
    demo: "https://devpost.com/software/carbonproxy",
    details: {
      company: "HackDuke 2026",
      period: "Feb 2026",
      responsibilities: [
        "Won Best Overall Hack at HackDuke 2026 for carbon-aware middleware that optimizes LLM inference through semantic prompt compression and intelligent routing.",
        "Designed an EcoStack semantic KV-caching layer that reduced session token bloat by 86%, from 2,400 to 340 tokens.",
        "Built CRUD lifecycle services with FastAPI and SQLite, including asynchronous background tasks for summarization and embedding persistence.",
        "Optimized inference efficiency with semantic KV lookup using cosine-similarity thresholding to inject only the most relevant cached values into context.",
      ],
    },
  },
  {
    id: 4,
    title: "Symbiote",
    description:
      "Voice-driven AI inspection ecosystem for Caterpillar, combining hands-free workflows and real-time anomaly detection.",
    image: Symbiote,
    tags: ["Gemini Live", "Qwen2-VL", "Modal", "Swift", "FastAPI", "Supabase"],
    github: "https://github.com/sujaldeshmukh1012/Symbiote",
    demo: "https://devpost.com/software/symbiote",
    details: {
      company: "HackIllinois 2026",
      period: "Feb 2026",
      responsibilities: [
        "Received Best Use of AI by Reach Capital and MLH plus an Honorable Mention at HackIllinois.",
        "Engineered a hands-free inspection workflow with Gemini Live voice orchestration and Qwen2-VL-7B anomaly detection, reducing a 20-minute manual process to an automated loop.",
        "Designed a GPU-accelerated routing layer on Modal and a full-stack Supabase and React system to automate inventory procurement and generate PDF audit trails.",
      ],
    },
  },
  {
    id: 5,
    title: "Adaptive Cloud & Network Resource Management",
    description:
      "Reinforcement-learning analysis of cloud autoscaling strategies that reduced resource waste without hurting cloudlet completion rates.",
    image: CloudRL,
    tags: [
      "Python",
      "PyTorch",
      "NumPy",
      "Gymnasium",
      "CloudSim Plus",
      "Py4J",
    ],
    github: "https://github.com/l3hargav/CloudSim-Resource-Management-RL",
    demo: "",
    details: {
      company: "Independent Research",
      period: "Aug 2025",
      responsibilities: [
        "Conducted a comparative analysis of RL algorithms showing agents prioritized high-density utilization and reduced resource waste by nearly 40% without hurting cloudlet completion rates.",
        "Implemented PPO, A2C, and DDQN agents from scratch in Python to simulate cloud autoscaling and evaluate convergence under realistic workloads.",
        "Built a hybrid environment with CloudSim Plus and a Python wrapper for agent-environment interaction.",
      ],
    },
  },
  {
    id: 6,
    title: "LockedIn - Job Application Tracker",
    description:
      "A job-application tracker with modular REST APIs, SQL-driven data pipelines, and application security controls.",
    image: LockedIn,
    tags: ["React.js", "PHP", "MySQL", "JavaScript", "Git"],
    github: "",
    demo: "",
    details: {
      company: "Team Project",
      period: "Jan 2025 - Aug 2025",
      responsibilities: [
        "Built backend services with modular REST APIs and SQL-driven data pipelines, including schema design, query optimization, and transaction-safe operations.",
        "Developed automation for data ingestion, document processing, and record updates.",
        "Added input validation, sanitization, auth logic, and protections against SQL injection, XSS, and request flooding.",
      ],
    },
  },
  {
    id: 7,
    title: "SharePlay",
    description:
      "A collaborative music platform where hosts run live sessions and guests queue, vote, and search songs in real time.",
    image: SharePlay,
    tags: ["React", "TypeScript", "Python", "Flask", "Firebase", "Spotify Web API"],
    github: "",
    demo: "https://share-play-three.vercel.app/",
    details: {
      company: "Independent Project",
      period: "Jul 2025 - Present",
      responsibilities: [
        "Built a collaborative music platform where hosts run live sessions and guests queue, vote, and search songs in real time.",
        "Developed a full-stack architecture with React, TypeScript, Django, and MongoDB, integrating Spotify OAuth 2.0 and Spotify Web API.",
        "Implemented an adaptive recommendation engine using exploration-exploitation algorithms to balance discovery and user preference.",
      ],
    },
  },
  {
    id: 8,
    title: "ResQme",
    description:
      "HackHarvard-winning ESP32 mesh network for disaster and off-grid communication.",
    image: ResQme,
    tags: ["TypeScript", "Node.js", "Supabase", "ESP32", "C/C++", "ESP-NOW"],
    github: "",
    demo: "https://devpost.com/software/resqme-jq52al",
    details: {
      company: "HackHarvard 2025",
      period: "Oct 2025",
      responsibilities: [
        "Won at HackHarvard 2025 for a compact, infrastructure-free ESP32 mesh network for disaster and off-grid communication.",
        "Architected a fault-tolerant ESP32 stack with master election, encrypted JSON payloads, GPS telemetry, SOS triggers, and prioritized emergency-alert broadcasts.",
        "Built an admin dashboard for first responders and an onboarding app for users to sync ESP devices and perform wireless OTA updates.",
      ],
    },
  },
  {
    id: 9,
    title: "Tiresias",
    description:
      "Edge AI assistive navigation system for visually impaired users with sub-100ms latency.",
    image: Tiresias,
    tags: ["Swift", "Python", "FastAPI", "YOLOv8", "Ollama", "WebSockets"],
    github: "",
    demo: "https://devpost.com/software/walking-cane",
    details: {
      company: "Hackathon Project",
      period: "Jan 2026",
      responsibilities: [
        "Built an edge AI assistive navigation system for the visually impaired using a wireless edge-cloud architecture with sub-100ms latency.",
        "Developed a Swift iOS client with AVFoundation and Network Framework to stream compressed video over persistent WebSockets.",
        "Engineered a hardware-accelerated FastAPI, PyTorch, and MPS backend for object detection and scene understanding.",
      ],
    },
  },
  {
    id: 10,
    title: "The Watcher",
    description:
      "A hybrid AI surveillance pipeline pairing local YOLOv8/OpenCV pre-screening with Gemini verification, cutting cloud API calls by 85-95%.",
    image: Watcher,
    tags: ["FastAPI", "Gemini Vision", "React", "Supabase", "YOLOv8", "OpenCV"],
    github: "",
    demo: "https://devpost.com/software/the-watcher-pn4lik",
    details: {
      company: "Hackathon Project",
      period: "Nov 2025",
      responsibilities: [
        "Designed The Watcher to solve the high cost of real-time surveillance with a hybrid pipeline combining local YOLOv8/OpenCV pre-screening and Google Gemini verification, reducing cloud API calls by 85-95%.",
        "Integrated the Gemini Vision API for context-aware scene analysis on high-confidence frames, achieving 96-98% verification accuracy while filtering false positives.",
        "Built a responsive dashboard using React, Vite, and Tailwind CSS with real-time video feeds, canvas overlays, and smart buffering via the MediaRecorder API to capture evidence context.",
        "Developed a high-performance REST API using FastAPI and Uvicorn to orchestrate video processing and manage threat metadata in PostgreSQL via Supabase.",
      ],
    },
  },
  {
    id: 11,
    title: "Cybersecurity Infrastructure Deployment",
    description:
      "A secured segmented enterprise network with Active Directory, server services, and firewall controls.",
    image: CyberSec,
    tags: ["pfSense", "Windows Server", "MariaDB"],
    github: "",
    demo: "",
    details: {
      company: "University Project",
      period: "Apr 2025 - May 2025",
      responsibilities: [
        "Deployed and secured a segmented enterprise network with pfSense, Windows Server 2022, Ubuntu 24.04, and Rocky Linux 8.10.",
        "Configured Active Directory, domain controller services, DNS, GPOs, and domain joining.",
        "Analyzed network traffic with Wireshark to identify malware behavior and mitigated threats through pfSense firewall rules.",
      ],
    },
  },
  {
    id: 12,
    title: "MM-Wave Sensor for Wound Analysis",
    description:
      "A HIPAA-compliant, contactless wound-imaging application using millimeter-wave radar, signal processing, and machine learning.",
    image: MMwave,
    tags: ["Python", "PyQt6", "scikit-learn", "pandas"],
    github: "",
    demo: "",
    details: {
      company: "Research with Dr. Wenyao Xu",
      period: "Feb 2024 - Aug 2024",
      responsibilities: [
        "Conducted research under Dr. Wenyao Xu to develop a HIPAA-compliant PyQt6 application for contactless wound imaging with millimeter-wave radar hardware.",
        "Implemented real-time signal processing and machine learning to generate 3D wound reconstructions and detect sub-surface inflammation and fluid accumulation.",
        "Built secure patient-data systems and parametric scan interfaces with automated calibration and temporal scan comparisons.",
      ],
    },
  },
  {
    id: 13,
    title: "Handwritten Digit & Face Classification using NNs",
    description:
      "Neural-network implementations and evaluations for handwritten-digit and face classification.",
    image: NeuralNet,
    tags: ["Python", "NumPy", "SciPy", "PyTorch"],
    github: "",
    demo: "",
    details: {
      company: "University Project",
      period: "Feb 2025 - Mar 2025",
      responsibilities: [
        "Implemented a single-layer feedforward neural network from scratch to classify handwritten digits.",
        "Built and evaluated deep neural networks and convolutional neural networks using PyTorch on the CelebA dataset.",
        "Performed an end-to-end ML pipeline covering preprocessing, feature selection, hyperparameter tuning, and technical presentation.",
      ],
    },
  },
  {
    id: 14,
    title: "Gun Classification CNN Model",
    description:
      "A CNN-based weapon-classification model evaluated on complex, real-world datasets.",
    image: CNN,
    tags: ["Python", "OpenCV", "TensorFlow", "PyTorch"],
    github: "",
    demo: "",
    details: {
      company: "Independent Research",
      period: "Dec 2024 - Jan 2025",
      responsibilities: [
        "Evaluated existing solutions for weapon classification on real-life datasets and identified a sharp accuracy drop on sanitized data.",
        "Engineered a CNN model that reached 70% accuracy on complex datasets, surpassing baseline models by 59%.",
      ],
    },
  },
    {
    id: 15,
    title: "Dynamic Memory Allocator",
    description:
      "Designed and implemented a custom dynamic memory allocator in C, mirroring the functionality of the standard 'malloc', 'free', and related memory management functions.",
    image: Allocator,
    tags: [
      "C",
      "Memory Management Algorithms",
      "Thread-Safety",
      "Performance Benchmarking",
    ],
    github: "",
    demo: "",
    details: {
      company: "CSE 220",
      period: "Fall 2023",
      responsibilities: [
        "mployed best-fit, worst-fit, and first-fit algorithms for efficient memory allocation and reduced fragmentation, benchmarked against the standard libc allocator. Incorporated coalescing and splitting techniques to optimize memory utilization and minimize external fragmentation. Implemented thread-safe memory allocation and deallocation using mutex locks to ensure safe concurrent operations in multi-threaded applications.",
        "Developed comprehensive unit tests to validate functionality, memory leaks, and boundary conditions, ensuring robustness and reliability.",
      ],
    },
  },
  {
    id: 16,
    title: "LearnEasy",
    description:
      "Gemini-powered accessible learning platform enabling hands-free education, AI-driven STEM accessibility, and adaptive multimodal learning experiences for students of all abilities.",
    image: LearnEasy,
    tags: [
      "React",
      "TypeScript",
      "Node.js",
      "PostgreSQL",
      "Gemini Live API",
      "Gemini 2.0 Flash",
      "Gemini TTS",
      "Imagen",
      "WebSockets",
      "LibLouis"
    ],
    github: "https://github.com/octomaanav/Learnhub",
    demo: "https://devpost.com/software/learnhub-9swaqb",
    details: {
      company: "Google Gemini API Competition",
      period: "Jan 2026 - Feb 2026",
      responsibilities: [
        "Designed and developed LearnEasy, an accessibility-first educational platform that removes physical, sensory, and cognitive barriers through AI-powered voice navigation, adaptive learning workflows, and multimodal content delivery.",
        "Engineered a real-time voice agent using Gemini Live API and tool-calling workflows, enabling students with motor impairments to navigate lessons, quizzes, and educational resources entirely through natural language commands.",
        "Built an automated STEM accessibility pipeline leveraging Gemini 2.0 Flash, LaTeX validation, LibLouis, and Nemeth Code translation to convert complex mathematical equations and scientific content into accurate Braille representations.",
        "Implemented adaptive micro-learning capabilities that automatically decompose complex topics into granular learning modules, reducing cognitive overload and improving learning outcomes for students with ADHD and cognitive disabilities.",
        "Integrated Gemini TTS, multilingual support, and Imagen-powered educational diagram generation to provide personalized visual, auditory, and language-accessible learning experiences across diverse learner needs.",
        "Architected a scalable full-stack platform using React, TypeScript, Node.js, PostgreSQL, and WebSockets to support low-latency AI interactions, real-time content updates, and accessibility-focused user experiences."
      ]
    }

  },
];

const ProjectsSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Add these state variables
  const [open, setOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  // Add state for the notification popup
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");

  // Add state for PDF viewer modal
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");

  // Functions to handle opening and closing the modal
  const handleOpen = (project) => {
    setSelectedProject(project);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Handle PDF viewer
  const handleOpenPDF = (url) => {
    setPdfUrl(url);
    setPdfOpen(true);
  };

  const handleClosePDF = () => {
    setPdfOpen(false);
    setPdfUrl("");
  };

  // Function to handle empty links
  const handleEmptyLink = (type) => {
    setNotificationMessage(
      `School project - Contact me for ${type} information.`,
    );
    setNotificationOpen(true);
  };

  // Function to check if URL is a PDF
  const isPDF = (url) => {
    return url && (url.toLowerCase().endsWith(".pdf") || url.includes("/pdf"));
  };

  // Function to handle demo/report link
  const handleDemoClick = (url) => {
    if (!url) {
      handleEmptyLink("demo");
    } else if (isPDF(url)) {
      handleOpenPDF(url);
    } else {
      window.open(url, "_blank");
    }
  };

  // Function to close the notification
  const handleNotificationClose = () => {
    setNotificationOpen(false);
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: isMobile ? 1 : isTablet ? 2 : 3,
    slidesToScroll: 1,
    autoplay: false,
    pauseOnHover: true,
    arrows: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerMode: true,
          centerPadding: "0", // No padding to ensure exact card width
          arrows: true,
          adaptiveHeight: true,
        },
      },
    ],
  };

  return (
    <AnimatedSection id="projects">
      <Container maxWidth="lg">
        <Box sx={{ pt: 10, pb: { xs: 12, sm: 8 } }}>
          {" "}
          {/* Extra bottom padding on mobile for dots */}
          <Typography
            variant="h3"
            component="h2"
            sx={{
              mb: 2,
              pb: 2,
              textAlign: "center",
              background: "linear-gradient(90deg, #FFFFFF 0%, #A78BFA 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Projects
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 8,
              textAlign: "center",
              color: "rgba(255,255,255,0.7)",
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            Here are a few projects I've worked on recently. Want to see more?
            Check out my GitHub.
          </Typography>
          <Box
            sx={{
              // Container styling
              px: { xs: 0, md: 4 },
              mx: { xs: 0 },
              position: "relative",

              // Slide styling
              ".slick-slide": {
                px: { xs: 0, sm: 2 }, // No padding on mobile slides
              },

              // Track alignment
              ".slick-track": {
                display: "flex",
                alignItems: "center",
              },

              // Arrow styling and positioning
              ".slick-prev, .slick-next": {
                zIndex: 2,
                height: "40px",
                width: "40px",
                "&:before": {
                  fontSize: { xs: "24px", sm: "30px" },
                  color: "#7C3AED",
                },
              },
              ".slick-prev": {
                left: { xs: "-8px", sm: -15, md: -30 }, // Positioned just outside the card
              },
              ".slick-next": {
                right: { xs: "-8px", sm: -15, md: -30 }, // Positioned just outside the card
              },

              // Dots styling
              ".slick-dots": {
                bottom: { xs: -45, sm: -25 },
              },
              ".slick-dots li button:before": {
                color: "#7C3AED",
                fontSize: "10px",
              },
              ".slick-dots li.slick-active button:before": {
                color: "#7C3AED",
                opacity: 1,
              },

              // List styling
              ".slick-list": {
                margin: { xs: "0 auto", sm: 0 }, // Center the list on mobile
                width: { xs: "calc(100% - 40px)", sm: "100%" }, // Make space for arrows
              },
            }}
          >
            <Slider {...settings}>
              {projects.map((project) => (
                <Box key={project.id} sx={{ width: "100%" }}>
                  <Card
                    sx={{
                      height: { xs: "auto", sm: "100%" }, // let slider control height on mobile, full height on larger screens
                      minHeight: { xs: 420, sm: 450 }, // maintain a reasonable minimum
                      maxHeight: { sm: 450 }, // constrain max height on desktop/tablet
                      display: "flex",
                      flexDirection: "column",
                      backgroundColor: "rgba(15, 15, 26, 0.85)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      transition: "transform 0.3s, box-shadow 0.3s",
                      "&:hover": {
                        transform: { xs: "none", sm: "translateY(-10px)" },
                        boxShadow: {
                          xs: "none",
                          sm: "0 10px 20px rgba(124, 58, 237, 0.2)",
                        },
                      },
                      mx: "auto",
                      width: "100%",
                      maxWidth: { xs: 320, sm: 400 },
                      position: "relative",
                    }}
                  >
                    {project.image ? (
                      <CardMedia
                        component="img"
                        height="180"
                        image={project.image}
                        alt={project.title}
                        sx={{
                          objectFit: "cover",
                          width: "100%",
                          borderTopLeftRadius: "4px",
                          borderTopRightRadius: "4px",
                          backgroundColor: "#222",
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Box
                        aria-label={`${project.title} project placeholder`}
                        sx={{
                          height: 180,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background:
                            "linear-gradient(135deg, rgba(124,58,237,0.35), rgba(15,15,26,0.95))",
                          borderTopLeftRadius: "4px",
                          borderTopRightRadius: "4px",
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            color: "#C4B5FD",
                            fontFamily: "monospace",
                            fontSize: "1.25rem",
                            fontWeight: 700,
                          }}
                        >
                          &lt;/&gt; {project.placeholder || "MCP"}
                        </Typography>
                      </Box>
                    )}
                    <CardContent
                      sx={{
                        flexGrow: 1,
                        pb: { xs: 1, sm: 2 },
                        display: "flex",
                        flexDirection: "column",
                        overflowY: "auto", // enable vertical scrolling when content is long
                        WebkitOverflowScrolling: "touch", // smooth scrolling on iOS
                        // calculate maxHeight so CardContent doesn't push CardActions off-screen
                        maxHeight: { sm: "calc(450px - 180px - 72px)" },
                        // hide scrollbar while still allowing scroll
                        "&::-webkit-scrollbar": { width: "6px" },
                        "&::-webkit-scrollbar-thumb": {
                          background: "rgba(124,58,237,0.15)",
                          borderRadius: "4px",
                        },
                      }}
                    >
                      <Typography
                        gutterBottom
                        variant="h5"
                        component="h3"
                        sx={{
                          color: "white",
                          // Ensure long titles fit
                          fontSize: { xs: "1.25rem", sm: "1.5rem" },
                          lineHeight: 1.2,
                          mb: 1,
                        }}
                      >
                        {project.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 2,
                          fontSize: { xs: "0.85rem", sm: "0.875rem" },
                          color: "rgba(255, 255, 255, 0.7)", // Light white for description text
                          // Add ellipsis for long descriptions
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          lineHeight: 1.5,
                        }}
                      >
                        {project.description}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: {
                            xs: "nowrap",
                            sm: "wrap",
                          },
                          gap: 1,
                          mb: 2,
                          overflow: {
                            xs: "auto",
                            sm: "visible",
                          },
                          pb: {
                            xs: 1,
                            sm: 0,
                          },
                          // Hide scrollbar
                          "&::-webkit-scrollbar": {
                            display: "none",
                          },
                          msOverflowStyle: "none",
                          scrollbarWidth: "none",
                          mt: "auto", // Push tags to bottom of content area
                        }}
                      >
                        {project.tags.map((tag, index) => (
                          <Box
                            key={index}
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "16px",
                              fontSize: "0.7rem", // Slightly smaller font
                              backgroundColor: "rgba(124, 58, 237, 0.1)",
                              color: "#7C3AED",
                              border: "1px solid #7C3AED",
                              whiteSpace: { xs: "nowrap", sm: "normal" },
                              flexShrink: 0, // Prevent tags from shrinking
                            }}
                          >
                            {tag}
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                    <CardActions
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        px: 2, // Add some horizontal padding
                        mt: "auto", // Push to bottom of card
                        flexWrap: { xs: "wrap", sm: "nowrap" }, // Allow wrapping on mobile
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          flexWrap: { xs: "nowrap", sm: "nowrap" }, // Don't wrap buttons
                        }}
                        s
                      >
                        <Button
                          size="small"
                          startIcon={<GitHubIcon />}
                          onClick={() =>
                            project.github
                              ? window.open(project.github, "_blank")
                              : handleEmptyLink("code")
                          }
                          sx={{
                            color: "rgba(255,255,255,0.7)",
                            "&:hover": { color: "#7C3AED" },
                            minWidth: { xs: "70px", sm: "auto" }, // Smaller min-width on mobile
                            fontSize: { xs: "0.75rem", sm: "0.8125rem" }, // Smaller font on mobile
                            cursor: "pointer",
                          }}
                        >
                          Code
                        </Button>
                        <Button
                          size="small"
                          startIcon={<LaunchIcon />}
                          onClick={() => handleDemoClick(project.demo)}
                          sx={{
                            color: "rgba(255,255,255,0.7)",
                            "&:hover": { color: "#7C3AED" },
                            minWidth: { xs: "70px", sm: "auto" }, // Smaller min-width on mobile
                            fontSize: { xs: "0.75rem", sm: "0.8125rem" }, // Smaller font on mobile
                            cursor: "pointer",
                          }}
                        >
                          Demo
                        </Button>
                      </Box>

                      {/* Make the view more button more mobile-friendly */}
                      <IconButton
                        onClick={() => handleOpen(project)}
                        size="small"
                        aria-label="view details"
                        sx={{
                          color: "rgba(124,58,237,0.7)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            color: "#7C3AED",
                            transform: "scale(1.1)",
                          },
                          ml: { xs: 0, sm: 1 }, // Add margin on desktop
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Box>
              ))}
            </Slider>
          </Box>
        </Box>
      </Container>

      {/* Modal for project details */}
      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        aria-labelledby="project-details-modal"
        aria-describedby="project-detailed-information"
      >
        <Fade in={open}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: "80%", md: "600px" },
              bgcolor: "rgba(18, 18, 18, 0.95)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(124, 58, 237, 0.3)",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              maxHeight: "80vh",
              overflow: "auto",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(0,0,0,0.1)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(124,58,237,0.3)",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(124,58,237,0.5)",
              },
            }}
          >
            {selectedProject && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h4"
                    component="h2"
                    sx={{ color: "#7C3AED" }}
                  >
                    {selectedProject.title}
                  </Typography>
                  <IconButton
                    onClick={handleClose}
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      "&:hover": { color: "#7C3AED" },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>

                {selectedProject.details.company && (
                  <Typography
                    variant="subtitle1"
                    sx={{ color: "rgba(255,255,255,0.9)", mb: 1 }}
                  >
                    {selectedProject.details.company}
                  </Typography>
                )}

                {selectedProject.details.period && (
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "rgba(255,255,255,0.7)", mb: 3 }}
                  >
                    {selectedProject.details.period}
                  </Typography>
                )}

                <Typography
                  variant="body1"
                  sx={{ color: "rgba(255,255,255,0.9)", mb: 3 }}
                >
                  {selectedProject.description}
                </Typography>

                <Typography variant="h6" sx={{ color: "#7C3AED", mb: 2 }}>
                  Key Responsibilities:
                </Typography>

                <List>
                  {selectedProject.details.responsibilities.map(
                    (item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <Box component="span" sx={{ color: "#7C3AED", mr: 1 }}>
                          •
                        </Box>
                        <ListItemText
                          primary={item}
                          primaryTypographyProps={{
                            sx: { color: "rgba(255,255,255,0.9)" },
                          }}
                        />
                      </ListItem>
                    ),
                  )}
                </List>

                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    mt: 4,
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<GitHubIcon />}
                    href={selectedProject.github}
                    target="_blank"
                    sx={{
                      borderColor: "rgba(124,58,237,0.5)",
                      color: "rgba(255,255,255,0.9)",
                      "&:hover": {
                        borderColor: "#7C3AED",
                        backgroundColor: "rgba(124,58,237,0.1)",
                      },
                    }}
                  >
                    View Code
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<LaunchIcon />}
                    href={selectedProject.demo}
                    target="_blank"
                    sx={{
                      backgroundColor: "rgba(124,58,237,0.8)",
                      color: "#121212",
                      "&:hover": {
                        backgroundColor: "#7C3AED",
                      },
                    }}
                  >
                    Live Demo
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Fade>
      </Modal>

      {/* Notification Snackbar */}
      <Snackbar
        open={notificationOpen}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity="info"
          sx={{ width: "100%" }}
        >
          {notificationMessage}
        </Alert>
      </Snackbar>

      {/* PDF Viewer Modal */}
      <Modal
        open={pdfOpen}
        onClose={handleClosePDF}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1300,
        }}
      >
        <Box
          sx={{
            width: { xs: "95%", sm: "90%", md: "85%" },
            height: { xs: "80vh", md: "90vh" },
            maxWidth: "1200px",
            backgroundColor: "#1a1a1a",
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          {/* Close button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              p: 2,
              borderBottom: "1px solid rgba(124,58,237,0.2)",
            }}
          >
            <IconButton
              onClick={handleClosePDF}
              sx={{
                color: "rgba(255,255,255,0.7)",
                "&:hover": { color: "#7C3AED" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* PDF Iframe */}
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <iframe
              src={`${pdfUrl}#toolbar=1&navpanes=0`}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                borderBottomLeftRadius: "8px",
                borderBottomRightRadius: "8px",
              }}
              title="PDF Viewer"
            />
          </Box>
        </Box>
      </Modal>
    </AnimatedSection>
  );
};

export default ProjectsSection;
